<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Active Visitors</h5>
        <p class="page-sub">Visitors currently on site</p>
      </div>
      <div class="page-actions">
        <button v-if="selected.length" class="btn-bulk-out" @click="promptBulkCheckout">
          <font-awesome-icon icon="sign-out-alt" />
          Check Out ({{ selected.length }})
        </button>
        <button class="btn-ghost" @click="exportPdf" :disabled="exportingPdf">
          <font-awesome-icon icon="file-pdf" /> {{ exportingPdf ? 'Exporting…' : 'Export PDF' }}
        </button>
        <button class="btn-ghost" @click="load"><font-awesome-icon icon="sync-alt" /> Refresh</button>
        <router-link :to="$url('/hr/visitors/check-in')" class="btn-primary-action">
          <font-awesome-icon icon="sign-in-alt" /> Check In
        </router-link>
      </div>
    </div>

    <!-- Public check-in URL banner -->
    <div v-if="publicCheckInUrl" class="public-url-bar">
      <div class="public-url-left">
        <font-awesome-icon icon="link" class="public-url-icon" />
        <div>
          <div class="public-url-heading">Public Visitor Check-In Link</div>
          <div class="public-url-value">{{ publicCheckInUrl }}</div>
        </div>
      </div>
      <button class="btn-copy" :class="{ 'btn-copy--copied': urlCopied }" @click="copyUrl">
        <font-awesome-icon :icon="urlCopied ? 'check' : 'copy'" />
        {{ urlCopied ? 'Copied!' : 'Copy Link' }}
      </button>
    </div>

    <!-- Search -->
    <div class="filter-bar">
      <input v-model="search" class="filter-input" placeholder="Search visitor, company, host, purpose…" style="min-width:260px;" />
      <span v-if="search && filteredVisits.length !== visits.length" class="chip chip--blue" style="font-size:12px;">
        {{ filteredVisits.length }} of {{ visits.length }} shown
      </span>
    </div>

    <!-- Skeleton -->
    <div v-if="loading" class="sk-table">
      <div v-for="n in 5" :key="n" class="sk-row"></div>
    </div>

    <!-- Empty -->
    <div v-else-if="!filteredVisits.length" class="empty-state">
      <font-awesome-icon icon="door-open" class="empty-icon" />
      <p>{{ search ? 'No visitors match your search.' : 'No visitors currently on site.' }}</p>
    </div>

    <!-- Table -->
    <div v-else class="table-card">
      <table class="data-table">
        <thead>
          <tr>
            <th class="col-check">
              <input
                type="checkbox"
                class="row-check"
                :checked="allSelected"
                :indeterminate.prop="someSelected"
                @change="toggleAll"
              />
            </th>
            <th>Badge</th>
            <th>Visitor</th>
            <th>Company</th>
            <th>Host</th>
            <th>Purpose</th>
            <th>Location</th>
            <th>Locker</th>
            <th>Checked In</th>
            <th>Duration</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="v in filteredVisits" :key="v.id" :class="{ 'row--selected': selected.includes(v.id) }">
            <td class="col-check">
              <input type="checkbox" class="row-check" :value="v.id" v-model="selected" />
            </td>
            <td><span class="chip chip--indigo">{{ v.badge_no }}</span></td>
            <td>
              <div class="vis-cell">
                <div class="vis-avatar">{{ initials(v.visitor) }}</div>
                <div>
                  <div class="vis-name">{{ v.visitor?.first_name }} {{ v.visitor?.last_name }}</div>
                  <div class="vis-sub">{{ v.visitor?.email }}</div>
                </div>
              </div>
            </td>
            <td>{{ v.visitor?.company || '—' }}</td>
            <td>{{ hostName(v.host, v) }}</td>
            <td>{{ v.purpose }}</td>
            <td>{{ v.location || v.site?.name || '—' }}</td>
            <td>
              <span v-if="v.locker" class="chip chip--teal">
                <font-awesome-icon icon="box" style="margin-right:3px;" />{{ v.locker.number }}
              </span>
              <span v-else class="text-muted">—</span>
            </td>
            <td>{{ fmtDatetime(v.checked_in_at) }}</td>
            <td>{{ duration(v.checked_in_at) }}</td>
            <td>
              <div style="display:flex;gap:4px;align-items:center;">
                <button class="btn-icon" title="View Details" @click="openDetail(v)">
                  <font-awesome-icon icon="eye" />
                </button>
                <button class="btn-icon" title="View Pass" @click="viewBadge(v)">
                  <font-awesome-icon icon="id-badge" />
                </button>
                <button class="btn-icon btn-icon--red" title="Check Out" @click="promptCheckout(v)" :disabled="checkingOut === v.id">
                  <font-awesome-icon icon="sign-out-alt" />
                </button>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <!-- Stats -->
    <div v-if="visits.length" style="margin-top:14px;display:flex;align-items:center;gap:10px;flex-wrap:wrap;">
      <span class="chip chip--green">
        <font-awesome-icon icon="users" style="margin-right:5px;" />
        {{ visits.length }} visitor{{ visits.length !== 1 ? 's' : '' }} on site
      </span>
      <span v-if="selected.length" class="chip chip--amber">
        {{ selected.length }} selected
      </span>
    </div>

    <!-- ── Visitor Detail modal ──────────────────────────────────────── -->
    <div v-if="detailVisit" class="modal-overlay" @click.self="detailVisit = null">
      <div class="modal-box">
        <div class="modal-head">
          <h5 class="modal-title">Visitor Details</h5>
          <button class="modal-close" @click="detailVisit = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" style="gap:0;padding:0;">

          <!-- Identity strip -->
          <div class="vd-identity">
            <div class="vd-identity-left">
              <div class="vd-avatar-lg">
                <img v-if="detailVisit.photo_url || detailVisit.visitor?.photo_url" :src="detailVisit.photo_url || detailVisit.visitor?.photo_url" class="vd-avatar-img" alt="" />
                <span v-else>{{ initials(detailVisit.visitor) }}</span>
              </div>
              <div>
                <div class="vd-full-name">{{ detailVisit.visitor?.first_name }} {{ detailVisit.visitor?.last_name }}</div>
                <div v-if="detailVisit.visitor?.company" class="vd-org">{{ detailVisit.visitor.company }}</div>
                <div class="vd-contact-row">
                  <span v-if="detailVisit.visitor?.email" class="vd-contact-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>
                    {{ detailVisit.visitor.email }}
                  </span>
                  <span v-if="detailVisit.visitor?.phone" class="vd-contact-item">
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.13 9.14a19.79 19.79 0 01-3.07-8.67A2 2 0 012.07 .5H5a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 8.36a16 16 0 006.29 6.29l1.42-1.42a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/></svg>
                    {{ detailVisit.visitor.phone }}
                  </span>
                </div>
              </div>
            </div>
            <span class="chip chip--green" style="flex-shrink:0;align-self:flex-start;">Checked In</span>
          </div>

          <!-- Visit info -->
          <div class="vd-section">
            <div class="vd-section-title">Visit Information</div>
            <div class="vd-grid">
              <div class="vd-field">
                <div class="vd-label">Badge No.</div>
                <div class="vd-value"><span class="chip chip--indigo">{{ detailVisit.badge_no }}</span></div>
              </div>
              <div class="vd-field">
                <div class="vd-label">Host</div>
                <div class="vd-value">{{ hostName(detailVisit.host, detailVisit) }}</div>
              </div>
              <div class="vd-field">
                <div class="vd-label">Purpose</div>
                <div class="vd-value">{{ detailVisit.purpose || '—' }}</div>
              </div>
              <div class="vd-field">
                <div class="vd-label">Location</div>
                <div class="vd-value">{{ detailVisit.location || detailVisit.site?.name || '—' }}</div>
              </div>
              <div v-if="detailVisit.locker" class="vd-field">
                <div class="vd-label">Locker</div>
                <div class="vd-value">
                  <span class="chip chip--teal"><font-awesome-icon icon="box" style="margin-right:3px;" />{{ detailVisit.locker.number }}</span>
                  <span v-if="detailVisit.locker.label" style="font-size:11px;color:#7a90b0;margin-left:6px;">{{ detailVisit.locker.label }}</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Check-in info -->
          <div class="vd-section">
            <div class="vd-section-title">Check-In</div>
            <div class="vd-grid">
              <div class="vd-field">
                <div class="vd-label">Checked In At</div>
                <div class="vd-value">{{ fmtDatetime(detailVisit.checked_in_at) }}</div>
              </div>
              <div class="vd-field">
                <div class="vd-label">Time on Site</div>
                <div class="vd-value">{{ duration(detailVisit.checked_in_at) }}</div>
              </div>
              <div v-if="detailVisit.items_deposited" class="vd-field vd-field--full">
                <div class="vd-label">Items Deposited</div>
                <div class="vd-value">{{ detailVisit.items_deposited }}</div>
              </div>
            </div>
          </div>

        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="viewBadge(detailVisit); detailVisit = null">
            <font-awesome-icon icon="id-badge" /> View Pass
          </button>
          <button class="btn-primary-action" style="background:#dc2626;" @click="promptCheckout(detailVisit); detailVisit = null">
            <font-awesome-icon icon="sign-out-alt" /> Check Out
          </button>
          <button class="btn-ghost" @click="detailVisit = null">Close</button>
        </div>
      </div>
    </div>

    <!-- ── Visitor Pass modal ──────────────────────────────────────────── -->
    <div v-if="badgeVisit" class="badge-overlay" @click.self="badgeVisit = null">
      <div class="badge-modal-wrap">
        <div class="badge-modal-actions no-print">
          <button class="btn-primary-action" @click="printBadge">
            <font-awesome-icon icon="print" /> Print Pass
          </button>
          <button class="btn-ghost" @click="badgeVisit = null">Close</button>
        </div>
        <div class="hbadge" id="hbadge-printable">
          <div class="hbadge-strip"></div>
          <div class="hbadge-content">
            <div class="hbadge-top">
              <div class="hbadge-company">
                <img v-if="company.logo" :src="company.logo" class="hbadge-logo" alt="" />
                <span class="hbadge-company-name">{{ company.name }}</span>
              </div>
              <span class="hbadge-type-label">VISITOR</span>
            </div>
            <div class="hbadge-identity">
              <div class="hbadge-photo-wrap">
                <img v-if="badgeVisit.photo_url || badgeVisit.visitor?.photo_url" :src="badgeVisit.photo_url || badgeVisit.visitor?.photo_url" class="hbadge-photo" alt="" />
                <div v-else class="hbadge-initials">{{ badgeInitials }}</div>
              </div>
              <div class="hbadge-name-block">
                <div class="hbadge-name">{{ badgeVisit.visitor?.first_name }} {{ badgeVisit.visitor?.last_name }}</div>
                <div v-if="badgeVisit.visitor?.company" class="hbadge-org">{{ badgeVisit.visitor.company }}</div>
                <div v-if="badgeVisit.visitor?.email" class="hbadge-email">{{ badgeVisit.visitor.email }}</div>
              </div>
            </div>
            <div class="hbadge-no-strip">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#c0cad6" stroke-width="1.8"><rect x="3" y="4" width="2" height="16"/><rect x="7" y="4" width="1" height="16"/><rect x="10" y="4" width="3" height="16"/><rect x="15" y="4" width="1" height="16"/><rect x="18" y="4" width="3" height="16"/></svg>
              <span class="hbadge-no">{{ badgeVisit.badge_no }}</span>
            </div>
            <div class="hbadge-details">
              <div class="hbadge-detail">
                <span class="hbd-label">Visiting</span>
                <span class="hbd-value">{{ badgeVisit.host ? hostName(badgeVisit.host) : (badgeVisit.host_name || '—') }}</span>
              </div>
              <div class="hbadge-detail">
                <span class="hbd-label">Purpose</span>
                <span class="hbd-value">{{ badgeVisit.purpose }}</span>
              </div>
              <div class="hbadge-detail">
                <span class="hbd-label">Check-in time</span>
                <span class="hbd-value">{{ fmtDatetime(badgeVisit.checked_in_at) }}</span>
              </div>
            </div>
            <div class="hbadge-instruction">
              Please wear this pass visibly at all times. Present to reception when leaving.
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Single checkout confirm modal -->
    <div v-if="confirmVisit" class="modal-overlay" @click.self="confirmVisit = null">
      <div class="modal-box" style="max-width:420px;">
        <div class="modal-head">
          <h5 class="modal-title">Check Out Visitor</h5>
          <button class="modal-close" @click="confirmVisit = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#556;margin:0;">
            Are you sure you want to check out
            <strong>{{ confirmVisit.visitor?.first_name }} {{ confirmVisit.visitor?.last_name }}</strong>?
          </p>
          <div class="detail-grid">
            <div class="detail-row">
              <span class="detail-label">Badge</span>
              <span class="detail-value">{{ confirmVisit.badge_no }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Host</span>
              <span class="detail-value">{{ hostName(confirmVisit.host, confirmVisit) }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Duration</span>
              <span class="detail-value">{{ duration(confirmVisit.checked_in_at) }}</span>
            </div>
          </div>
          <div v-if="confirmVisit.badge_no" class="badge-return-row">
            <label class="badge-return-label">
              <input type="checkbox" v-model="badgeReturned" />
              Badge returned by visitor
            </label>
            <span v-if="!badgeReturned" class="badge-return-warn">
              <font-awesome-icon icon="exclamation-triangle" /> Badge will be flagged as outstanding
            </span>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="confirmVisit = null; badgeReturned = true">Cancel</button>
          <button class="btn-primary-action" style="background:#dc2626;" @click="confirmCheckout" :disabled="checkingOut === confirmVisit.id">
            <font-awesome-icon v-if="checkingOut === confirmVisit.id" icon="spinner" spin />
            {{ checkingOut === confirmVisit.id ? 'Checking out…' : 'Confirm Check Out' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Bulk checkout confirm modal -->
    <div v-if="showBulkModal" class="modal-overlay" @click.self="showBulkModal = false">
      <div class="modal-box" style="max-width:460px;">
        <div class="modal-head">
          <h5 class="modal-title">Bulk Check Out</h5>
          <button class="modal-close" @click="showBulkModal = false"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body">
          <p style="font-size:13px;color:#556;margin:0 0 14px;">
            You are about to check out <strong>{{ selected.length }} visitor{{ selected.length !== 1 ? 's' : '' }}</strong>. This cannot be undone.
          </p>
          <div class="bulk-list">
            <div v-for="v in selectedVisits" :key="v.id" class="bulk-list-row">
              <div class="vis-avatar" style="width:28px;height:28px;font-size:11px;">{{ initials(v.visitor) }}</div>
              <div style="flex:1;min-width:0;">
                <div class="vis-name">{{ v.visitor?.first_name }} {{ v.visitor?.last_name }}</div>
                <div class="vis-sub">{{ v.badge_no }} · {{ duration(v.checked_in_at) }}</div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="showBulkModal = false">Cancel</button>
          <button class="btn-primary-action" style="background:#dc2626;" @click="confirmBulkCheckout" :disabled="bulkingOut">
            <font-awesome-icon v-if="bulkingOut" icon="spinner" spin />
            {{ bulkingOut ? 'Checking out…' : `Check Out ${selected.length}` }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import jsPDF from 'jspdf'
import 'jspdf-autotable'

export default {
  name: 'VisitorActive',
  setup() { return { toast: useToast() } },
  data() {
    return {
      visits: [], loading: true, checkingOut: null, confirmVisit: null,
      urlCopied: false, currentBusinessId: null,
      badgeVisit: null, detailVisit: null,
      company: { name: '', logo: null },
      selected: [],
      showBulkModal: false,
      bulkingOut: false,
      exportingPdf: false,
      search: '',
      badgeReturned: true,
    }
  },
  computed: {
    filteredVisits() {
      const q = this.search.trim().toLowerCase()
      if (!q) return this.visits
      return this.visits.filter(v => {
        const name    = `${v.visitor?.first_name ?? ''} ${v.visitor?.last_name ?? ''}`.toLowerCase()
        const company = (v.visitor?.company ?? '').toLowerCase()
        const host    = `${v.host?.first_name ?? ''} ${v.host?.last_name ?? ''} ${v.host_name ?? ''}`.toLowerCase()
        const purpose = (v.purpose ?? '').toLowerCase()
        return name.includes(q) || company.includes(q) || host.includes(q) || purpose.includes(q)
      })
    },
    publicCheckInUrl() {
      const code  = this.$route.params.companyCode
      const bizId = this.currentBusinessId || this.$store.state.getLoggedInUser?.business_id
      if (!bizId) return null
      return `${window.location.origin}/visitor-checkin/${code}/${bizId}`
    },
    badgeInitials() {
      const v = this.badgeVisit?.visitor
      return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase()
    },
    allSelected() {
      return this.visits.length > 0 && this.selected.length === this.visits.length
    },
    someSelected() {
      return this.selected.length > 0 && this.selected.length < this.visits.length
    },
    selectedVisits() {
      return this.visits.filter(v => this.selected.includes(v.id))
    },
  },
  created() {
    this.load()
    const user    = this.$store.state.getLoggedInUser
    const baseUrl = (axios.defaults.baseURL || '').replace(/\/api\/?$/, '')
    this.company  = {
      name: user?.business_name ?? '',
      logo: user?.business_logo ? `${baseUrl}/${user.business_logo}` : null,
    }
  },
  methods: {
    async copyUrl() {
      try {
        await navigator.clipboard.writeText(this.publicCheckInUrl)
        this.urlCopied = true
        setTimeout(() => { this.urlCopied = false }, 2500)
      } catch {
        this.urlCopied = false
      }
    },
    async load() {
      this.loading = true
      this.selected = []
      try {
        const r = await axios.get('visitors/active')
        const d = r.data.data
        this.visits = d?.visits ?? d ?? []
        if (d?.business_id) this.currentBusinessId = d.business_id
      } catch { this.toast.error('Failed to load active visitors.') }
      finally { this.loading = false }
    },
    toggleAll(e) {
      this.selected = e.target.checked ? this.visits.map(v => v.id) : []
    },
    promptCheckout(visit) {
      this.confirmVisit = visit
    },
    async confirmCheckout() {
      const visit = this.confirmVisit
      this.checkingOut = visit.id
      try {
        const payload = visit.badge_no ? { badge_returned: this.badgeReturned } : {}
        await axios.post(`visitors/${visit.id}/check-out`, payload)
        if (visit.badge_no && !this.badgeReturned) {
          this.toast.warning('Visitor checked out. Badge flagged as not returned.')
        } else {
          this.toast.success('Visitor checked out.')
        }
        this.confirmVisit = null
        this.badgeReturned = true
        this.selected = this.selected.filter(id => id !== visit.id)
        this.load()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to check out.')
      } finally { this.checkingOut = null }
    },
    promptBulkCheckout() {
      this.showBulkModal = true
    },
    async confirmBulkCheckout() {
      this.bulkingOut = true
      try {
        const r = await axios.post('visitors/bulk-check-out', { ids: this.selected })
        const count = r.data.data?.checked_out ?? this.selected.length
        this.toast.success(`${count} visitor${count !== 1 ? 's' : ''} checked out.`)
        this.showBulkModal = false
        this.selected = []
        this.load()
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Bulk check-out failed.')
      } finally { this.bulkingOut = false }
    },
    exportPdf() {
      this.exportingPdf = true
      try {
        const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' })

        doc.setFontSize(18)
        doc.setFont('helvetica', 'bold')
        doc.setTextColor(26, 43, 69)
        doc.text('Active Visitors', 40, 46)

        doc.setFontSize(9)
        doc.setFont('helvetica', 'normal')
        doc.setTextColor(122, 144, 176)
        const parts = []
        if (this.company.name) parts.push(this.company.name)
        parts.push(`${this.visits.length} visitor${this.visits.length !== 1 ? 's' : ''} on site`)
        parts.push(`Exported ${new Date().toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })}`)
        doc.text(parts.join('   ·   '), 40, 62)

        doc.autoTable({
          startY: 78,
          head: [['Badge', 'Visitor', 'Company', 'Host', 'Purpose', 'Location', 'Checked In', 'Duration']],
          body: this.visits.map(v => [
            v.badge_no || '—',
            `${v.visitor?.first_name ?? ''} ${v.visitor?.last_name ?? ''}`.trim() || '—',
            v.visitor?.company || '—',
            this.hostName(v.host, v),
            v.purpose || '—',
            v.location || v.site?.name || '—',
            this.fmtDatetime(v.checked_in_at),
            this.duration(v.checked_in_at),
          ]),
          styles: { fontSize: 8, cellPadding: 5, textColor: [26, 43, 69] },
          headStyles: { fillColor: [0, 74, 173], textColor: 255, fontStyle: 'bold', fontSize: 8 },
          alternateRowStyles: { fillColor: [248, 250, 252] },
          columnStyles: {
            0: { cellWidth: 55 },
            4: { cellWidth: 90 },
            5: { cellWidth: 70 },
            7: { cellWidth: 52 },
          },
          margin: { left: 40, right: 40 },
          didDrawPage(data) {
            doc.setFontSize(8)
            doc.setTextColor(192, 202, 214)
            doc.text(
              `Page ${data.pageNumber}`,
              doc.internal.pageSize.width / 2,
              doc.internal.pageSize.height - 16,
              { align: 'center' }
            )
          },
        })

        doc.save(`active-visitors-${new Date().toISOString().slice(0, 10)}.pdf`)
      } catch {
        this.toast.error('PDF export failed.')
      } finally {
        this.exportingPdf = false
      }
    },
    openDetail(visit) { this.detailVisit = visit },
    viewBadge(visit) {
      this.badgeVisit = visit
    },
    printBadge() {
      const el = document.getElementById('hbadge-printable')
      if (!el) return
      const win = window.open('', '_blank', 'width=620,height=900')
      win.document.write(`<!DOCTYPE html><html><head><title>Visitor Pass</title><style>
        *{box-sizing:border-box;margin:0;padding:0;}
        body{background:#f4f7fb;display:flex;align-items:center;justify-content:center;min-height:100vh;font-family:-apple-system,BlinkMacSystemFont,'Segoe UI',sans-serif;}
        .hbadge{width:480px;background:#fff;border-radius:20px;box-shadow:0 24px 64px rgba(0,0,0,.18);display:flex;overflow:hidden;}
        .hbadge-strip{width:8px;background:linear-gradient(180deg,#004AAD 0%,#1565d8 100%);flex-shrink:0;}
        .hbadge-content{flex:1;padding:24px 24px 20px;}
        .hbadge-top{display:flex;align-items:center;justify-content:space-between;margin-bottom:20px;}
        .hbadge-company{display:flex;align-items:center;gap:8px;}
        .hbadge-logo{height:24px;width:auto;object-fit:contain;background:#fff;border-radius:4px;padding:2px;}
        .hbadge-company-name{font-size:13px;font-weight:700;color:#1a2b45;}
        .hbadge-type-label{font-size:9px;font-weight:800;letter-spacing:.14em;color:#fff;background:#004AAD;padding:3px 9px;border-radius:4px;}
        .hbadge-identity{display:flex;align-items:center;gap:16px;margin-bottom:20px;}
        .hbadge-photo{width:72px;height:72px;border-radius:12px;object-fit:cover;border:2px solid #e0e7ff;flex-shrink:0;}
        .hbadge-initials{width:72px;height:72px;border-radius:12px;background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%);color:#fff;font-size:24px;font-weight:800;display:flex;align-items:center;justify-content:center;flex-shrink:0;}
        .hbadge-name-block{min-width:0;}
        .hbadge-name{font-size:20px;font-weight:800;color:#1a2b45;line-height:1.2;margin-bottom:2px;}
        .hbadge-org{font-size:12px;color:#7a90b0;margin-bottom:1px;}
        .hbadge-email{font-size:11px;color:#aab8cc;}
        .hbadge-no-strip{display:flex;align-items:center;gap:10px;background:#f4f7fb;border-radius:8px;padding:9px 14px;margin-bottom:16px;}
        .hbadge-no{font-size:16px;font-weight:800;color:#1a2b45;letter-spacing:.08em;}
        .hbadge-details{display:flex;flex-direction:column;border-top:1px solid #f0f4f8;margin-bottom:16px;}
        .hbadge-detail{display:flex;justify-content:space-between;align-items:baseline;padding:9px 0;border-bottom:1px solid #f0f4f8;}
        .hbadge-detail:last-child{border-bottom:none;}
        .hbd-label{font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:.05em;color:#aab8cc;}
        .hbd-value{font-size:13px;font-weight:600;color:#1a2b45;text-align:right;max-width:65%;}
        .hbadge-instruction{font-size:11px;color:#c0cad6;text-align:center;line-height:1.5;padding-top:12px;border-top:1px solid #f0f4f8;}
        @media print{body{background:#fff;min-height:auto;}.hbadge{box-shadow:none;border:1px solid #e4eaf2;}}
      </style></head><body>${el.outerHTML}</body></html>`)
      win.document.close()
      win.focus()
      setTimeout(() => { win.print(); win.close() }, 300)
    },
    duration(checkedInAt) {
      if (!checkedInAt) return '—'
      const mins = Math.floor((Date.now() - new Date(checkedInAt)) / 60000)
      if (mins < 60) return `${mins}m`
      return `${Math.floor(mins / 60)}h ${mins % 60}m`
    },
    initials(v) { return ((v?.first_name?.[0] ?? '') + (v?.last_name?.[0] ?? '')).toUpperCase() },
    hostName(h, visit) {
      if (h) return `${h.first_name} ${h.last_name}`
      return visit?.host_name || '—'
    },
    fmtDatetime(v) {
      if (!v) return '—'
      return new Date(v).toLocaleString('en-GB', { dateStyle: 'short', timeStyle: 'short' })
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; gap:8px; align-items:center; flex-wrap:wrap; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; text-decoration:none; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; text-decoration:none; }
.btn-ghost:hover:not(:disabled) { background:#e4eaf2; }
.btn-ghost:disabled { opacity:.6; cursor:not-allowed; }
.btn-bulk-out { display:inline-flex; align-items:center; gap:6px; background:#dc2626; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; }
.btn-bulk-out:hover { background:#b91c1c; }
.btn-icon { background:none; border:none; cursor:pointer; color:#7a90b0; padding:5px 8px; border-radius:6px; font-size:14px; }
.btn-icon:hover { background:#f0f4f8; color:#004AAD; }
.btn-icon--red { color:#7a90b0; }
.btn-icon--red:hover { background:#fef2f2; color:#dc2626; }

.filter-bar { display:flex; align-items:center; gap:10px; flex-wrap:wrap; margin-bottom:14px; }
.filter-input { padding:8px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; font-family:inherit; }
.filter-input:focus { border-color:#004AAD; }

.col-check { width:40px; padding-left:14px !important; }
.row-check { width:15px; height:15px; cursor:pointer; accent-color:#004AAD; }

.table-card { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.data-table { width:100%; border-collapse:collapse; font-size:13px; }
.data-table thead th { padding:11px 14px; text-align:left; font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; background:#f8fafc; border-bottom:1px solid #e4eaf2; white-space:nowrap; }
.data-table tbody tr { border-bottom:1px solid #f0f4f8; transition:background .1s; }
.data-table tbody tr:last-child { border-bottom:none; }
.data-table tbody tr:hover { background:#f8fafc; }
.data-table tbody tr.row--selected { background:#eff6ff; }
.data-table tbody td { padding:11px 14px; color:#1a2b45; vertical-align:middle; font-size:13px; }
.empty-cell { text-align:center; color:#aab8cc; padding:40px !important; font-size:13px; }

.chip { display:inline-flex; align-items:center; padding:3px 10px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
.chip--green  { background:#dcfce7; color:#16a34a; }
.chip--teal   { background:#ccfbf1; color:#0f766e; }
.chip--indigo { background:#e0e7ff; color:#4338ca; }
.chip--amber  { background:#fef3c7; color:#d97706; }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box   { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:92vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-head  { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; line-height:1; }
.modal-body  { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.detail-grid { background:#f8fafc; border-radius:8px; padding:12px 14px; }
.detail-row  { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #f0f4f8; font-size:13px; }
.detail-row:last-child { border-bottom:none; }
.detail-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#aab8cc; }
.detail-value { font-weight:600; color:#1a2b45; font-size:13px; }

.bulk-list { background:#f8fafc; border-radius:8px; overflow:hidden; max-height:260px; overflow-y:auto; }
.bulk-list-row { display:flex; align-items:center; gap:10px; padding:9px 14px; border-bottom:1px solid #f0f4f8; font-size:13px; }
.bulk-list-row:last-child { border-bottom:none; }

.sk-table { background:#fff; border:1px solid #e4eaf2; border-radius:12px; overflow:hidden; }
.sk-row   { height:52px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-bottom:1px solid #e4eaf2; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

.empty-state { text-align:center; padding:60px 20px; color:#aab8cc; }
.empty-icon  { font-size:3rem; margin-bottom:14px; display:block; color:#dde5f0; }

.vis-avatar { width:32px; height:32px; border-radius:50%; background:#e0e7ff; display:flex; align-items:center; justify-content:center; font-size:12px; font-weight:700; color:#4338ca; flex-shrink:0; }
.vis-cell   { display:flex; align-items:center; gap:8px; }
.vis-name   { font-weight:600; font-size:13px; color:#1a2b45; }
.vis-sub    { font-size:11px; color:#7a90b0; }

.public-url-bar { display:flex; align-items:center; justify-content:space-between; gap:12px; flex-wrap:wrap; background:#fff; border:1px solid #e4eaf2; border-radius:12px; padding:14px 18px; margin-bottom:20px; }
.public-url-left { display:flex; align-items:center; gap:12px; min-width:0; }
.public-url-icon { color:#004AAD; font-size:14px; flex-shrink:0; }
.public-url-heading { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:2px; }
.public-url-value { font-size:13px; font-weight:600; color:#1a2b45; word-break:break-all; }
.btn-copy { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:12px; font-weight:600; padding:0 14px; height:34px; border-radius:8px; cursor:pointer; font-family:inherit; white-space:nowrap; flex-shrink:0; transition:background .15s,color .15s; }
.btn-copy:hover { background:#e4eaf2; }
.btn-copy--copied { background:#dcfce7; color:#16a34a; border-color:#bbf7d0; }

/* ── Badge modal overlay ──────────────────────────────────────────────────── */
.badge-overlay { position:fixed; inset:0; background:rgba(0,0,0,.55); z-index:1000; display:flex; align-items:center; justify-content:center; padding:20px; }
.badge-modal-wrap { display:flex; flex-direction:column; align-items:center; gap:16px; width:100%; max-width:500px; }
.badge-modal-actions { display:flex; gap:10px; }

/* ── Badge card ───────────────────────────────────────────────────────────── */
.hbadge { width:100%; background:#fff; border-radius:20px; box-shadow:0 24px 64px rgba(0,0,0,.22); display:flex; overflow:hidden; }
.hbadge-strip { width:8px; background:linear-gradient(180deg,#004AAD 0%,#1565d8 100%); flex-shrink:0; }
.hbadge-content { flex:1; padding:24px 24px 20px; }

.hbadge-top { display:flex; align-items:center; justify-content:space-between; margin-bottom:20px; }
.hbadge-company { display:flex; align-items:center; gap:8px; }
.hbadge-logo { height:24px; width:auto; object-fit:contain; background:#fff; border-radius:4px; padding:2px; }
.hbadge-company-name { font-size:13px; font-weight:700; color:#1a2b45; }
.hbadge-type-label { font-size:9px; font-weight:800; letter-spacing:.14em; color:#fff; background:#004AAD; padding:3px 9px; border-radius:4px; }

.hbadge-identity { display:flex; align-items:center; gap:16px; margin-bottom:20px; }
.hbadge-photo { width:72px; height:72px; border-radius:12px; object-fit:cover; border:2px solid #e0e7ff; flex-shrink:0; }
.hbadge-initials { width:72px; height:72px; border-radius:12px; background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%); color:#fff; font-size:24px; font-weight:800; display:flex; align-items:center; justify-content:center; flex-shrink:0; }
.hbadge-name-block { min-width:0; }
.hbadge-name  { font-size:20px; font-weight:800; color:#1a2b45; line-height:1.2; margin-bottom:2px; }
.hbadge-org   { font-size:12px; color:#7a90b0; margin-bottom:1px; }
.hbadge-email { font-size:11px; color:#aab8cc; }

.hbadge-no-strip { display:flex; align-items:center; gap:10px; background:#f4f7fb; border-radius:8px; padding:9px 14px; margin-bottom:16px; }
.hbadge-no { font-size:16px; font-weight:800; color:#1a2b45; letter-spacing:.08em; }

.hbadge-details { display:flex; flex-direction:column; border-top:1px solid #f0f4f8; margin-bottom:16px; }
.hbadge-detail { display:flex; justify-content:space-between; align-items:baseline; padding:9px 0; border-bottom:1px solid #f0f4f8; }
.hbadge-detail:last-child { border-bottom:none; }
.hbd-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#aab8cc; }
.hbd-value { font-size:13px; font-weight:600; color:#1a2b45; text-align:right; max-width:65%; }

.hbadge-instruction { font-size:11px; color:#c0cad6; text-align:center; line-height:1.5; padding-top:12px; border-top:1px solid #f0f4f8; }

/* ── Visitor Detail modal ─────────────────────────────────────────────────── */
.vd-identity { display:flex; align-items:flex-start; justify-content:space-between; gap:12px; padding:20px 22px; border-bottom:1px solid #f0f4f8; }
.vd-identity-left { display:flex; align-items:flex-start; gap:14px; flex:1; min-width:0; }
.vd-avatar-lg { width:52px; height:52px; border-radius:13px; flex-shrink:0; background:linear-gradient(135deg,#004AAD 0%,#1565d8 100%); color:#fff; font-size:18px; font-weight:800; display:flex; align-items:center; justify-content:center; overflow:hidden; }
.vd-avatar-img { width:100%; height:100%; object-fit:cover; }
.vd-full-name { font-size:16px; font-weight:800; color:#1a2b45; line-height:1.2; margin-bottom:3px; }
.vd-org { font-size:12px; color:#7a90b0; margin-bottom:5px; }
.vd-contact-row { display:flex; flex-wrap:wrap; gap:10px; }
.vd-contact-item { display:inline-flex; align-items:center; gap:5px; font-size:12px; color:#4a5e78; }
.vd-section { padding:16px 22px; border-bottom:1px solid #f0f4f8; }
.vd-section-title { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.06em; color:#aab8cc; margin-bottom:12px; }
.vd-grid { display:grid; grid-template-columns:1fr 1fr; gap:12px 20px; }
.vd-field { display:flex; flex-direction:column; gap:3px; }
.vd-field--full { grid-column:1 / -1; }
.vd-label { font-size:11px; font-weight:600; text-transform:uppercase; letter-spacing:.05em; color:#aab8cc; }
.vd-value { font-size:13px; font-weight:600; color:#1a2b45; line-height:1.5; }

/* Badge return */
.badge-return-row { margin-top:14px; padding:10px 12px; background:#f8fafc; border:1px solid #e4eaf2; border-radius:8px; display:flex; flex-direction:column; gap:6px; }
.badge-return-label { display:flex; align-items:center; gap:8px; font-size:13px; font-weight:500; color:#1a2b45; cursor:pointer; }
.badge-return-label input[type="checkbox"] { width:16px; height:16px; accent-color:#004AAD; cursor:pointer; flex-shrink:0; }
.badge-return-warn { font-size:11px; font-weight:600; color:#c2410c; display:flex; align-items:center; gap:5px; padding-left:24px; }
</style>
